// The Lobby's header, on every tab: the logo, the player's chip (who I am, my rank; it opens « Profil »),
// the language switch and the settings. LTR in every language: the language switch keeps its place
// (owner's rule). On a computer's large screen the tabs sit in it too. (The tutorial is in the training tile.)
import React from "react";
import { Settings } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { LanguageSwitch } from "../LanguageSwitch";
import { RankEmblem } from "../rank/RankEmblem";
import { RankBar, fmt, labelOf } from "../rank/rankParts";
import { LobbyTabsInline } from "./LobbyTabBar";
import { CALIBRATION_GAMES, rankInfo } from "../../lib/ranks";
import { LOGO_URL } from "../../lib/brand";
import { LANG_SWITCH, t } from "../../lib/i18n";

function PlayerChip({ user, rank, onClick }) {
  const info = rankInfo(rank);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t("nav.profile")}
      data-testid="player-chip"
      className="group flex min-w-0 items-center gap-2 rounded-2xl py-1 pe-2 ps-1 text-start transition-colors hover:bg-white/[0.04]"
    >
      <span className="relative shrink-0">
        <Avatar className="h-8 w-8 ring-2 ring-amber-400/60 ring-offset-2 ring-offset-slate-950 transition-shadow group-hover:ring-amber-300">
          <AvatarImage src={user.picture} />
          <AvatarFallback className="bg-emerald-900 text-amber-300">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>
        <RankEmblem info={info} size={18} className="absolute -bottom-1.5 -right-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]" />
      </span>
      <span className="min-w-0">
        <span className="block max-w-[96px] truncate text-sm leading-tight text-white sm:max-w-[180px]" dir="auto">{user.name}</span>
        <span className="flex items-center gap-1.5 text-[11px] leading-tight">
          <span className="truncate font-display italic text-amber-300" data-testid="player-chip-rank">{labelOf(info)}</span>
          <span className="shrink-0 tabular-nums text-slate-500">
            {info.calibrating ? `${info.games}/${CALIBRATION_GAMES}` : fmt(info.rp)}
          </span>
        </span>
        <RankBar info={info.calibrating ? { progress: info.games / CALIBRATION_GAMES } : info} className="mt-1 h-1 w-20" />
      </span>
    </button>
  );
}

export function LobbyHeader({ user, rank, top = 0, tab, onTab, badges, onProfile, onSettings }) {
  return (
    <header
      className="sticky z-30 border-b border-white/5 bg-slate-950/80 backdrop-blur-xl"
      style={{ top }}
    >
      <div dir="ltr" className="mx-auto flex max-w-6xl items-center gap-1.5 px-3 py-1.5 sm:gap-2 sm:px-6">
        <img src={LOGO_URL} alt="" className="hidden h-8 w-8 shrink-0 rounded-lg ring-1 ring-amber-400/30 sm:block" />
        <PlayerChip user={user} rank={rank} onClick={onProfile} />
        <div className="flex flex-1 justify-center">
          <LobbyTabsInline tab={tab} onTab={onTab} badges={badges} />
        </div>
        {LANG_SWITCH && <LanguageSwitch />}
        <button
          type="button"
          onClick={onSettings}
          aria-label={t("settings.title")}
          title={t("settings.title")}
          data-testid="settings-btn"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-slate-900/60 text-slate-300 transition-colors hover:border-amber-500/40 hover:text-amber-200"
        >
          <Settings className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}

export default LobbyHeader;
